import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { changeMyPassword, getMyProfile, updateMyProfile } from '../../lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

export default function EmployeeProfile() {
  const { user, token, updateProfile: updateAuthProfile } = useAuth();
  const location = useLocation();
  const isPassword = location.pathname.includes('/employee/profile/password');
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!user) return;
    setProfile({
      full_name: user.full_name || user.name || '',
      email: user.email || '',
      phone: user.phone || '',
    });
  }, [user]);

  useEffect(() => {
    const hydrateProfile = async () => {
      if (!token) return;
      try {
        const resp = await getMyProfile(token);
        const current = resp?.data;
        if (current) {
          setProfile({
            full_name: current.full_name || current.name || '',
            email: current.email || '',
            phone: current.phone || '',
          });
        }
      } catch {
        // Keep fallback from auth context when API refresh fails.
      }
    };
    hydrateProfile();
  }, [token]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      if (token) {
        const resp = await updateMyProfile(token, profile);
        updateAuthProfile(resp?.data || profile);
      } else {
        updateAuthProfile(profile);
      }
      toast.success('Profile updated successfully.');
    } catch (err) {
      toast.error(err?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdatePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    if (!token) {
      toast.error('Please login again and retry.');
      return;
    }
    setSaving(true);
    try {
      await changeMyPassword(token, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      updateAuthProfile({ must_change_password: false });
      toast.success('Password updated successfully.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err?.message || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="employee-profile">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {isPassword ? 'Change Password' : 'My Profile'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isPassword ? 'Update your account password' : 'View and update your profile details'}
          </p>
          {user?.must_change_password && isPassword && (
            <p className="text-sm text-amber-600 mt-2">
              You are signed in with a temporary password. Update it now to secure your account.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant={isPassword ? 'outline' : 'default'}>
            <Link to="/employee/profile">Profile</Link>
          </Button>
          <Button asChild size="sm" variant={isPassword ? 'default' : 'outline'}>
            <Link to="/employee/profile/password">Change Password</Link>
          </Button>
        </div>
      </div>

      {!isPassword ? (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your employee profile details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input id="full_name" name="full_name" value={profile.full_name} onChange={handleProfileChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={user?.employee_role_name || user?.role || 'Employee'}
                  readOnly
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" value={profile.email} onChange={handleProfileChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" name="phone" value={profile.phone} onChange={handleProfileChange} />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
              <span className="text-sm text-muted-foreground">
                Department: {user?.department_name || 'N/A'}
              </span>
            </div>

            <div className="flex gap-3">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setProfile({
                    full_name: user?.full_name || user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                  });
                }}
              >
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Use a strong password and keep it secure</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  name="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="Enter current password"
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  name="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter new password"
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordChange}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleUpdatePassword} disabled={saving}>
                {saving ? 'Updating...' : 'Update Password'}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/employee/profile">Cancel</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
